import { useState, useEffect, useCallback } from "react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { fmtTimestamp } from "@/lib/format";
import { toast } from "sonner";
import { AlertDialog, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
const labels = { "reset-password": "Invia reset password", disable: "Disattiva account", reactivate: "Riattiva account", "revoke-sessions": "Revoca sessioni" };
export const TesterManager = () => {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [query, setQuery] = useState("");
  const [search, setSearch] = useState("");
  const [offset, setOffset] = useState(0);
  const [more, setMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [pending, setPending] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    setLoading(true); setError("");
    try { const { data } = await api.get("/auth/support/accounts", { params: { q: search, offset } }); setRows(data.items); setMore(data.has_more); }
    catch (e) { setError(apiErr(e)); }
    finally { setLoading(false); }
  }, [search, offset]);
  useEffect(() => { load(); }, [load]);
  const apply = async () => {
    setBusy(true);
    try {
      await api.post('/auth/testers/' + pending.user.id + '/' + pending.action);
      toast.success(pending.action === "reset-password" ? "Richiesta accettata dal servizio email" : "Operazione completata");
      setPending(null); await load();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };
  return <div className="space-y-4" data-testid="tester-manager">
    <form className="flex gap-2" onSubmit={e => { e.preventDefault(); setOffset(0); setSearch(query.trim()); }}>
      <Input aria-label="Cerca email, centro o ID" placeholder="Email, centro o ID" maxLength={200} value={query} onChange={e => setQuery(e.target.value)} />
      <Button type="submit" disabled={loading}>Cerca</Button>
    </form>
    {loading ? <p role="status">Caricamento account…</p> : error ? <div role="alert"><p>{error}</p><Button onClick={load}>Riprova</Button></div> : <>
      {!rows.length && <p>Nessun account corrispondente.</p>}
      {rows.map(u => <article key={u.id} className="rounded-xl border p-4 space-y-3">
        <div className="break-words"><h2 className="font-bold">{u.email}</h2><p>{u.tenant_name || "Centro non indicato"}</p></div>
        <dl className="text-sm grid sm:grid-cols-2 gap-2 break-all">
          <div><dt className="text-muted-foreground">Account</dt><dd>{u.status === "active" ? "Attivo" : "Disattivato"}</dd></div>
          <div><dt className="text-muted-foreground">Centro</dt><dd>{u.tenant_status || "Stato non disponibile"}</dd></div>
          <div><dt className="text-muted-foreground">ID account</dt><dd>{u.id}</dd></div>
          <div><dt className="text-muted-foreground">ID centro</dt><dd>{u.tenant_id || "Non assegnato"}</dd></div>
          {u.created_at && <div><dt>Creato</dt><dd>{fmtTimestamp(u.created_at)}</dd></div>}
          {u.last_login_at && <div><dt>Ultimo accesso</dt><dd>{fmtTimestamp(u.last_login_at)}</dd></div>}
          <div><dt>Sessioni attive</dt><dd>{u.active_sessions}</dd></div>
          <div><dt>Google Calendar</dt><dd>{u.calendar?.connected ? (u.calendar.sync_enabled ? "Collegato, sincronizzazione attiva" : "Collegato, sincronizzazione sospesa") : "Non collegato"}</dd></div>
        </dl>
        <div className="flex flex-wrap gap-2">
          {["reset-password", u.status === "active" ? "disable" : "reactivate", "revoke-sessions"].map(action => <Button key={action} variant="outline" disabled={busy || (u.id === user?.id) || (action === "reset-password" && u.status !== "active")} onClick={() => setPending({user:u,action})}>{labels[action]}</Button>)}
        </div>
        {u.id === user?.id && <p className="text-xs text-muted-foreground">Non disponibile sul tuo account amministratore</p>}
      </article>)}
      <div className="flex gap-3"><Button variant="outline" disabled={!offset} onClick={() => setOffset(x => Math.max(0,x-50))}>Precedenti</Button><Button variant="outline" disabled={!more} onClick={() => setOffset(x => x+50)}>Successivi</Button></div>
    </>}
    <AlertDialog open={!!pending} onOpenChange={open => { if (!open && !busy) setPending(null); }}>
      <AlertDialogContent><AlertDialogHeader><AlertDialogTitle>{labels[pending?.action]}</AlertDialogTitle>
        <AlertDialogDescription asChild><div className="space-y-2 break-words"><p>{pending?.user.email} — {pending?.user.tenant_name}</p>
          <p>{pending?.action === "reset-password" ? "Invieremo un link monouso all'utente. Ogni link di reset precedente sarà invalidato." : pending?.action === "reactivate" ? "L'utente potrà accedere nuovamente. I dati del centro restano invariati." : "Le sessioni precedenti saranno revocate. Nessun dato del centro verrà eliminato."}</p></div></AlertDialogDescription>
      </AlertDialogHeader><AlertDialogFooter><AlertDialogCancel disabled={busy}>Annulla</AlertDialogCancel><Button onClick={apply} disabled={busy}>{busy ? "Attendi…" : "Conferma"}</Button></AlertDialogFooter></AlertDialogContent>
    </AlertDialog>
  </div>;
};
